/**
 * PDF builder — assembles selected menu cards into a single multi-page A6 PDF.
 *
 * Pipeline per booking:
 *   1. Render the chosen template to an off-screen <div> using React 18's
 *      `flushSync` for guaranteed-synchronous render.
 *   2. Query the resulting <svg> element from the DOM.
 *   3. Hand that <svg> to svg2pdf, which translates SVG draw commands into
 *      jsPDF vector commands on the current A6 page. No rasterization —
 *      type stays crisp at any DPI.
 *   4. addPage() and repeat for the next booking.
 *
 * Output: a Blob — the caller decides whether to download (doc.save-style)
 * or open in a new tab (Preview button).
 *
 * Why createRoot + flushSync rather than renderToStaticMarkup?
 *   - svg2pdf is more reliable with elements that are attached to the DOM
 *     (it occasionally needs computed values that only resolve in-tree).
 *   - keeps react-dom/server out of the bundle (~30KB gzipped saving).
 *   - flushSync is purpose-built for "render synchronously, just this once"
 *     and the dev-mode warning it emits is acceptable for a one-shot job.
 *
 * Failure handling: per-booking try/catch. A bad render is logged in the
 * `failed` array on the result and the loop continues — one weird booking
 * doesn't kill a 60-card print job.
 */
import { jsPDF } from "jspdf";
import { svg2pdf } from "svg2pdf.js";
import { createRoot, type Root } from "react-dom/client";
import { flushSync } from "react-dom";
import { renderTemplate } from "./templates";
import type {
  CardSize,
  MenuCardBooking,
  MenuCardPrintOptions,
  MenuCardServiceHeader,
} from "./types";

// Page geometry (mm) per card size — must match the templates' viewBox.
// A5 is landscape (same height as A6, double the width).
const PAGE_DIMS: Record<
  CardSize,
  { w: number; h: number; format: "a6" | "a5"; orientation: "portrait" | "landscape" }
> = {
  a6: { w: 105, h: 148, format: "a6", orientation: "portrait" },
  a5: { w: 210, h: 148, format: "a5", orientation: "landscape" },
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface BuildPdfArgs {
  bookings: MenuCardBooking[];
  options: MenuCardPrintOptions;
  /**
   * Optional progress callback fired after each booking's page is committed.
   * `current` is 1-based; `total` is bookings.length.
   */
  onProgress?: (current: number, total: number) => void;
}

export interface BuildPdfResult {
  blob: Blob;
  /** Number of pages successfully written. */
  pageCount: number;
  /**
   * Bookings whose template render or svg2pdf conversion threw. Each is
   * skipped — the resulting PDF simply has fewer pages than `bookings.length`.
   */
  failed: { booking: MenuCardBooking; error: Error }[];
}

/**
 * Build a multi-page A6 PDF from a list of bookings and a set of print
 * options. One page per booking, in the order supplied. Caller picks
 * whether to download or preview the resulting Blob.
 */
export async function buildMenuCardsPdf(args: BuildPdfArgs): Promise<BuildPdfResult> {
  if (args.bookings.length === 0) {
    throw new Error("No bookings to render — pick at least one.");
  }

  // Off-screen mount point. Far enough off-screen that even RTL layouts
  // can't catch it; sized at native A6 mm so the SVG rendering is at
  // print-correct dimensions.
  const size: CardSize = args.options.size ?? "a6";
  const dims = PAGE_DIMS[size];

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-99999px";
  container.style.top = "0";
  container.style.width = `${dims.w}mm`;
  container.style.height = `${dims.h}mm`;
  container.style.pointerEvents = "none";
  container.setAttribute("aria-hidden", "true");
  container.setAttribute("data-menu-card-renderer", "");
  document.body.appendChild(container);

  // jsPDF: A6 portrait or A5 landscape, mm units. With our SVG also authored
  // in mm at the same extents, the svg2pdf transform is a clean 1:1 (no
  // scaling). putOnlyUsedFonts + compress shave several KB off a big output.
  const doc = new jsPDF({
    unit: "mm",
    format: dims.format,
    orientation: dims.orientation,
    putOnlyUsedFonts: true,
    compress: true,
  });

  let root: Root | null = null;
  const failed: BuildPdfResult["failed"] = [];

  try {
    root = createRoot(container);

    for (let i = 0; i < args.bookings.length; i++) {
      const booking = args.bookings[i]!;

      // First page is auto-created by jsPDF; subsequent need addPage.
      if (i > 0) {
        doc.addPage(dims.format, dims.orientation);
      }

      try {
        // Synchronous render. flushSync drains React updates before we
        // continue — by the next line the <svg> is in the container.
        flushSync(() => {
          root!.render(
            renderTemplate({
              booking,
              selection: args.options.template,
              printMode: args.options.printMode,
              highlightDietary: args.options.highlightDietary,
              scaleFoodToFit: args.options.scaleFoodToFit,
              size,
            })
          );
        });

        const svgEl = container.querySelector("svg") as SVGSVGElement | null;
        if (!svgEl) {
          throw new Error("Template did not produce an <svg> element");
        }

        // svg2pdf: convert this SVG to PDF commands. Width/height in mm
        // map onto the page directly — no scaling distortion.
        await svg2pdf(svgEl, doc, {
          x: 0,
          y: 0,
          width: dims.w,
          height: dims.h,
        });
      } catch (err) {
        failed.push({
          booking,
          error: err instanceof Error ? err : new Error(String(err)),
        });
        console.error(
          `[buildMenuCardsPdf] failed for booking ${booking.booking_reference} (${booking.account_name}):`,
          err
        );
        // Continue — the page added above will be left blank for this
        // booking, which is more honest than silently dropping it.
      }

      args.onProgress?.(i + 1, args.bookings.length);
    }

    const blob = doc.output("blob");
    return {
      blob,
      pageCount: args.bookings.length - failed.length,
      failed,
    };
  } finally {
    // Best-effort cleanup. If unmount throws (rare), the GC will still
    // collect the container once it's removed from the DOM.
    if (root) {
      try {
        root.unmount();
      } catch {
        /* ignore */
      }
    }
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}

// ---------------------------------------------------------------------------
// Filename helpers
// ---------------------------------------------------------------------------

/** Filename tag for a card size — "" for A6 (the historical default), "-a5" for A5. */
function sizeTag(size: CardSize | undefined): string {
  return size === "a5" ? "-a5" : "";
}

/**
 * Filename for a whole-service generation.
 * Example: pullman-menu-cards-2026-05-02-1235-spring-service.pdf
 *          pullman-menu-cards-a5-2026-05-02-1235-spring-service.pdf
 */
export function filenameForWholeService(
  service: MenuCardServiceHeader,
  size?: CardSize
): string {
  const date = service.service_date;
  // service_time_label is "2026-05-02 12:35" — pull the HH:MM half, drop colon
  const timeRaw = service.service_time_label.split(" ")[1] ?? "";
  const time = timeRaw.replace(":", "");
  const desc = sanitizeFilenameComponent(service.journey_description);
  return [
    `pullman-menu-cards${sizeTag(size)}`,
    date,
    time,
    desc,
  ]
    .filter(Boolean)
    .join("-")
    .replace(/-+/g, "-") // collapse multi-hyphens that fall out of the join
    + ".pdf";
}

/**
 * Filename for a single-card generation.
 * Example: menu-card-272387-gerhardt.pdf  /  menu-card-a5-272387-gerhardt.pdf
 */
export function filenameForSingleCard(
  booking: MenuCardBooking,
  size?: CardSize
): string {
  const ref = booking.booking_reference;
  const name = sanitizeFilenameComponent(booking.account_name);
  return `menu-card${sizeTag(size)}-${ref}-${name || "guest"}.pdf`;
}

function sanitizeFilenameComponent(s: string): string {
  return s
    .toLowerCase()
    .replace(/['"`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

// ---------------------------------------------------------------------------
// Browser plumbing — download + preview
// ---------------------------------------------------------------------------

/**
 * Trigger a browser download of the supplied Blob with the given filename.
 * No-op outside a browser environment (defensive — the modal only calls
 * this from a user click handler).
 */
export function downloadPdf(blob: Blob, filename: string): void {
  if (typeof document === "undefined") return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on a short delay so the browser has the URL committed to its
  // download queue before we yank it.
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/**
 * Open the PDF in a new browser tab. Used by the Preview button. Falls
 * back to a download if the popup is blocked (some browsers refuse to
 * open programmatic windows even from a click).
 */
export function previewPdf(blob: Blob, fallbackFilename = "menu-card-preview.pdf"): void {
  if (typeof window === "undefined") return;
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");
  if (!win) {
    // Popup blocked — fall back to a download
    URL.revokeObjectURL(url);
    // Re-create the URL because we just revoked. Inefficient but rare.
    const fallbackUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = fallbackUrl;
    a.download = fallbackFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(fallbackUrl), 1500);
    return;
  }
  // Revoke the URL after the new tab has had time to load (typically a few
  // hundred ms; we give a generous minute).
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
